import React, { createContext, useContext, useReducer, useEffect, ReactNode, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authenticatedApi } from '@/api/config';

// Types
interface User {
  id?: string;
  name?: string;
  email?: string;
  thumbnail?: string;
  title?: string;
  profileComplete?: boolean;
  categories?: any[];
  showProfileName?: boolean;
  showProfileImage?: boolean;
  gender?: string;
  bio?: string;
  template?: number | string | null;  // Add null to template field
  [key: string]: any;
}

interface UserState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

interface UserContextType extends UserState {
  setUser: (userData: User) => void;
  updateUser: (userUpdates: Partial<User>) => void;
  clearUser: () => void;
  refreshUser: () => void;  // Add refreshUser function
}

// Initial state
const initialState: UserState = {
  user: null,
  isLoading: true, // Start with loading true
  error: null,
};

// Action types
type UserAction =
  | { type: 'SET_USER'; payload: User }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'CLEAR_USER' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string };

// Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : action.payload as User,
      };
    case 'CLEAR_USER':
      return {
        ...state,
        user: null,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const { user: auth0User, isAuthenticated, isLoading: auth0Loading } = useAuth0();

  // Extract fetchUserData function so it can be called externally
  const fetchUserData = async () => {
    if (auth0Loading) {
      // Only set loading to false if we're not authenticated
      if (!isAuthenticated) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      return;
    }

    if (!isAuthenticated || !auth0User) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Fetch user data from backend using authenticatedApi
      const response = await authenticatedApi.get('/api/v1/user/');
      
      if (response && response.data) {
        // Map the backend user data to our user structure
        const backendUser: User = {
          id: response.data.id || response.data._id,
          name: response.data.name || auth0User.name,
          email: response.data.email || auth0User.email,
          thumbnail: response.data.thumbnail || auth0User.picture,
          title: response.data.title,
          // Handle both camelCase and snake_case for profileComplete
          profileComplete: response.data.profileComplete || response.data.profile_complete || false,
          categories: response.data.categories || [],
          showProfileName: response.data.showProfileName || response.data.show_profile_name,
          showProfileImage: response.data.showProfileImage || response.data.show_profile_image,
          gender: response.data.gender,
          bio: response.data.bio,
          template: response.data.template || null,  // Explicitly handle null template
          highlight_title: response.data.highlight_title,
          location_title: response.data.location_title,
          location_url: response.data.location_url,
          google_review_url: response.data.google_review_url,
          location_click_count: response.data.location_click_count,
          google_review_click_count: response.data.google_review_click_count,
        };

        dispatch({ type: 'SET_USER', payload: backendUser });
      } else {
        // Fallback to Auth0 user data
        const fallbackUser: User = {
          id: auth0User.sub || '',
          name: auth0User.name || '',
          email: auth0User.email || '',
          thumbnail: auth0User.picture,
          profileComplete: false,
          template: null,  // No default template
        };
        dispatch({ type: 'SET_USER', payload: fallbackUser });
      }
    } catch (error) {

      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch user data' });
    }
  };

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    
    // If we have a stored user and Auth0 is still loading, show the stored user temporarily
    if (storedUser && auth0Loading) {
      try {
        const user = JSON.parse(storedUser);
        dispatch({ type: 'SET_USER', payload: user });
      } catch {
        // Silently handle parsing error and clean up invalid data
        localStorage.removeItem('user');
      }
    }
  }, [auth0Loading]);

  // Fetch user data from backend when authenticated
  useEffect(() => {
    fetchUserData();
  }, [auth0User, isAuthenticated, auth0Loading]);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (state.user) {
      localStorage.setItem('user', JSON.stringify(state.user));
    } else {
      localStorage.removeItem('user');
    }
  }, [state.user]);

  const setUser = useMemo(
    () => (user: User) => {
      dispatch({ type: 'SET_USER', payload: user });
    },
    []
  );

  const updateUser = useMemo(
    () => (updates: Partial<User>) => {
      dispatch({ type: 'UPDATE_USER', payload: updates });
    },
    []
  );

  const clearUser = useMemo(
    () => () => {
      dispatch({ type: 'CLEAR_USER' });
    },
    []
  );

  // Create refreshUser function that calls fetchUserData
  const refreshUser = useMemo(
    () => () => {
      fetchUserData();
    },
    [fetchUserData]
  );

  const value: UserContextType = useMemo(
    () => ({
      ...state,
      setUser,
      updateUser,
      clearUser,
      refreshUser,  // Add refreshUser to the context value
    }),
    [state, setUser, updateUser, clearUser, refreshUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};